import { supabase } from '@/lib/supabase'

export interface ReminderConfig {
  id: string
  name: string
  description?: string
  channel: 'email' | 'whatsapp' | 'sms'
  hours_before: number
  enabled: boolean
  message_template: string
  created_by?: string
  created_at?: string
  updated_at?: string
}

export interface ReminderLog {
  id: string
  slot_id: string
  volunteer_email?: string
  config_id: string
  channel: 'email' | 'whatsapp' | 'sms'
  status: 'pending' | 'sent' | 'failed'
  error_message?: string
  sent_at?: string
  created_at?: string
}

export class ReminderServiceClass {
  async createReminderConfig(config: Omit<ReminderConfig, 'id' | 'created_at' | 'updated_at'>): Promise<ReminderConfig | null> {
    try {
      const { data, error } = await supabase
        .from('reminder_configs')
        .insert([config])
        .select()
        .single()

      if (error) throw error
      return data
    } catch (error) {
      console.error('Error creating reminder config:', error)
      throw error
    }
  }

  async getAllReminderConfigs(): Promise<ReminderConfig[]> {
    try {
      const { data, error } = await supabase
        .from('reminder_configs')
        .select('*')
        .order('created_at', { ascending: false })

      if (error) throw error
      return data || []
    } catch (error) {
      console.error('Error fetching reminder configs:', error)
      return []
    }
  }

  async getEnabledReminderConfigs(): Promise<ReminderConfig[]> {
    try {
      const { data, error } = await supabase
        .from('reminder_configs')
        .select('*')
        .eq('enabled', true)
        .order('hours_before', { ascending: true })

      if (error) throw error
      return data || []
    } catch (error) {
      console.error('Error fetching enabled reminder configs:', error)
      return []
    }
  }

  async updateReminderConfig(id: string, updates: Partial<ReminderConfig>): Promise<ReminderConfig | null> {
    try {
      const { data, error } = await supabase
        .from('reminder_configs')
        .update(updates)
        .eq('id', id)
        .select()
        .single()

      if (error) throw error
      return data
    } catch (error) {
      console.error('Error updating reminder config:', error)
      throw error
    }
  }

  async deleteReminderConfig(id: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('reminder_configs')
        .delete()
        .eq('id', id)

      if (error) throw error
      return true
    } catch (error) {
      console.error('Error deleting reminder config:', error)
      throw error
    }
  }

  async toggleReminderConfig(id: string, enabled: boolean): Promise<ReminderConfig | null> {
    return this.updateReminderConfig(id, { enabled })
  }

  async getReminderLogs(filters?: {
    status?: 'pending' | 'sent' | 'failed'
    volunteer_email?: string
    config_id?: string
    limit?: number
    offset?: number
  }): Promise<ReminderLog[]> {
    try {
      let query = supabase
        .from('reminder_logs')
        .select('*')

      if (filters?.status) {
        query = query.eq('status', filters.status)
      }
      if (filters?.volunteer_email) {
        query = query.eq('volunteer_email', filters.volunteer_email)
      }
      if (filters?.config_id) {
        query = query.eq('config_id', filters.config_id)
      }

      query = query.order('created_at', { ascending: false })

      if (filters?.limit) {
        query = query.limit(filters.limit)
      }
      if (filters?.offset) {
        query = query.range(filters.offset, (filters.offset || 0) + (filters.limit || 10) - 1)
      }

      const { data, error } = await query

      if (error) throw error
      return data || []
    } catch (error) {
      console.error('Error fetching reminder logs:', error)
      return []
    }
  }

  async getReminderStats(): Promise<{
    total: number
    sent: number
    failed: number
    pending: number
  }> {
    try {
      const { data, error } = await supabase
        .from('reminder_logs')
        .select('status')

      if (error) throw error

      const stats = {
        total: data?.length || 0,
        sent: data?.filter(r => r.status === 'sent').length || 0,
        failed: data?.filter(r => r.status === 'failed').length || 0,
        pending: data?.filter(r => r.status === 'pending').length || 0
      }

      return stats
    } catch (error) {
      console.error('Error fetching reminder stats:', error)
      return { total: 0, sent: 0, failed: 0, pending: 0 }
    }
  }

  async logReminder(log: Omit<ReminderLog, 'id' | 'created_at'>): Promise<ReminderLog | null> {
    try {
      const { data, error } = await supabase
        .from('reminder_logs')
        .insert([log])
        .select()
        .single()

      if (error) throw error
      return data
    } catch (error) {
      console.error('Error logging reminder:', error)
      throw error
    }
  }

  async updateReminderLogStatus(
    id: string,
    status: 'pending' | 'sent' | 'failed',
    errorMessage?: string
  ): Promise<ReminderLog | null> {
    try {
      const update: any = {
        status,
        sent_at: status === 'sent' ? new Date().toISOString() : null
      }
      if (errorMessage) {
        update.error_message = errorMessage
      }

      const { data, error } = await supabase
        .from('reminder_logs')
        .update(update)
        .eq('id', id)
        .select()
        .single()

      if (error) throw error
      return data
    } catch (error) {
      console.error('Error updating reminder log:', error)
      throw error
    }
  }

  async getUpcomingReminders(): Promise<Array<{ slot: any; config: ReminderConfig }>> {
    try {
      const configs = await this.getEnabledReminderConfigs()
      const upcoming = []

      for (const config of configs) {
        const now = new Date()
        const targetTime = new Date(now.getTime() + config.hours_before * 60 * 60 * 1000)

        const { data: slots, error } = await supabase
          .from('volunteer_slots')
          .select('*')
          .eq('date', targetTime.toISOString().split('T')[0])
          .not('volunteer_email', 'is', null)

        if (error) throw error

        if (slots) {
          for (const slot of slots) {
            const { data: existingLog } = await supabase
              .from('reminder_logs')
              .select('id')
              .eq('slot_id', slot.id)
              .eq('config_id', config.id)
              .single()

            if (!existingLog) {
              upcoming.push({ slot, config })
            }
          }
        }
      }

      return upcoming
    } catch (error) {
      console.error('Error fetching upcoming reminders:', error)
      return []
    }
  }

  formatMessage(template: string, slot: any): string {
    return template
      .replace('{parent_name}', slot.volunteer_name || 'הורה יקר')
      .replace('{date}', new Date(slot.date).toLocaleDateString('he-IL'))
      .replace('{time}', slot.time_slot || '07:30-08:10')
      .replace('{class_name}', slot.class_name || '')
  }
}

export const reminderService = new ReminderServiceClass()
